import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiRequest, invalidateCache } from '../../../shared/api/base'
import { ToggleSwitch } from '@tendbot/ui'

interface NotificationPrefs {
  approval: boolean
  briefing: boolean
  nudge: boolean
  email: boolean
  quietHours: {
    enabled: boolean
    startTime: string
    endTime: string
  }
}

const DEFAULT_PREFS: NotificationPrefs = {
  approval: true,
  briefing: true,
  nudge: true,
  email: true,
  quietHours: { enabled: false, startTime: '22:00', endTime: '07:00' },
}

type CategoryKey = 'approval' | 'briefing' | 'nudge' | 'email'

const CATEGORIES: { key: CategoryKey; labelKey: string }[] = [
  { key: 'approval', labelKey: 'notifications.approvals' },
  { key: 'briefing', labelKey: 'notifications.briefings' },
  { key: 'nudge', labelKey: 'notifications.nudges' },
  { key: 'email', labelKey: 'notifications.emails' },
]

export default function NotificationSettingsExpanded() {
  const { t } = useTranslation('settings')
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_PREFS)
  const [saving, setSaving] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  // Load current preferences on mount
  useEffect(() => {
    apiRequest<Record<string, unknown>>('/settings/preferences')
      .then((data) => {
        const notif = data.notifications as Partial<NotificationPrefs> | undefined
        if (notif) {
          setPrefs({
            ...DEFAULT_PREFS,
            ...notif,
            quietHours: { ...DEFAULT_PREFS.quietHours, ...notif.quietHours },
          })
        }
        setLoaded(true)
      })
      .catch(() => setLoaded(true))
  }, [])

  const saveNotifications = async (updates: Partial<NotificationPrefs>, key: string) => {
    setSaving(key)
    const prev = { ...prefs }
    const next = { ...prefs, ...updates }
    if (updates.quietHours) {
      next.quietHours = { ...prefs.quietHours, ...updates.quietHours }
    }
    setPrefs(next)

    try {
      await apiRequest('/settings/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ notifications: next }),
      })
      invalidateCache('/settings/preferences')
    } catch {
      setPrefs(prev)
    } finally {
      setSaving(null)
    }
  }

  const handleCategoryToggle = (key: CategoryKey) => {
    saveNotifications({ [key]: !prefs[key] }, key)
  }

  const handleQuietHoursToggle = () => {
    saveNotifications(
      { quietHours: { ...prefs.quietHours, enabled: !prefs.quietHours.enabled } },
      'quietHours',
    )
  }

  const handleTimeChange = (field: 'startTime' | 'endTime', value: string) => {
    saveNotifications(
      { quietHours: { ...prefs.quietHours, [field]: value } },
      field,
    )
  }

  if (!loaded) {
    return (
      <div className="space-y-3">
        <div className="h-4 w-32 bg-charcoal/50 rounded animate-shimmer" />
        <div className="h-4 w-48 bg-charcoal/50 rounded animate-shimmer" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-pearl/50 text-xs">{t('notifications.description')}</p>

      {/* Category toggles */}
      {CATEGORIES.map(({ key, labelKey }) => (
        <div
          key={key}
          className="flex items-center justify-between py-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-sm text-ivory">{t(labelKey)}</span>
          <ToggleSwitch
            checked={prefs[key]}
            onChange={(e) => {
              e?.stopPropagation()
              handleCategoryToggle(key)
            }}
            size="sm"
            saving={saving === key}
            disabled={saving !== null}
          />
        </div>
      ))}

      {/* Quiet hours */}
      <div className="border-t border-slate-mid/30 pt-3 mt-3">
        <div
          className="flex items-center justify-between py-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <div>
            <span className="text-sm text-ivory block">{t('notifications.quietHours')}</span>
            <span className="text-pearl/60 text-xs">{t('notifications.quietHoursDescription')}</span>
          </div>
          <ToggleSwitch
            checked={prefs.quietHours.enabled}
            onChange={(e) => {
              e?.stopPropagation()
              handleQuietHoursToggle()
            }}
            size="sm"
            saving={saving === 'quietHours'}
            disabled={saving !== null}
          />
        </div>

        {prefs.quietHours.enabled && (
          <div className="flex items-center gap-3 mt-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex-1">
              <label className="text-pearl/60 text-xs block mb-1">{t('notifications.from')}</label>
              <input
                type="time"
                value={prefs.quietHours.startTime}
                onChange={(e) => handleTimeChange('startTime', e.target.value)}
                className="w-full bg-charcoal/50 border border-slate-mid/30 rounded-lg px-3 py-1.5 text-sm text-ivory focus:border-gold/40 focus:outline-none"
              />
            </div>
            <div className="flex-1">
              <label className="text-pearl/60 text-xs block mb-1">{t('notifications.to')}</label>
              <input
                type="time"
                value={prefs.quietHours.endTime}
                onChange={(e) => handleTimeChange('endTime', e.target.value)}
                className="w-full bg-charcoal/50 border border-slate-mid/30 rounded-lg px-3 py-1.5 text-sm text-ivory focus:border-gold/40 focus:outline-none"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
